/**
 * Lamb Analytics SDK - 메인 SDK 클래스
 *
 * 글로벌 `la()` 함수를 제공하며, SDK 초기화 및 이벤트 전송을 관리합니다.
 */

import { SdkConfig, CONSENT_DEFAULT_GRANTED } from './config';
import { IdentityManager } from './identity';
import { EventQueue } from '../transport/queue';
import { EventSender } from '../transport/sender';
import { BatchProcessor } from '../transport/batch';
import { generateEventId } from '../utils/uuid';
import { getDeviceInfo, detectDeviceType, detectBrowser, detectOS } from '../utils/device';
import { getPageLocation, getPageTitle, getPageReferrer, getUtmParams } from '../utils/url';
import type { ClickIds } from '../utils/url';
import { AutoTrackManager } from '../tracking/auto-track';
import { ProductTracker } from '../ecommerce/product';
import { CartTracker } from '../ecommerce/cart';
import { PurchaseTracker } from '../ecommerce/purchase';

import type { ViewItemListParams, SelectItemParams, ViewItemParams, AddToWishlistParams } from '../ecommerce/product';
import type { CartEventParams } from '../ecommerce/cart';
import type { CheckoutParams, ShippingInfoParams, PaymentInfoParams, PurchaseParams, RefundParams } from '../ecommerce/purchase';

/** SDK 초기화 옵션 */
export interface LambAnalyticsOptions {
  debug?: boolean;
  autoTrack?: {
    pageView?: boolean;
    scroll?: boolean;
    outboundClick?: boolean;
    fileDownload?: boolean;
    siteSearch?: boolean;
    /**
     * 하트비트/이탈 추적(GA4 user_engagement 유사) — 체류시간·이탈시각 확정용.
     * - `true` 또는 생략: 기본값(하트비트 15초, 유휴 컷오프 5분)으로 활성
     * - `false`: 비활성
     * - `{ heartbeatIntervalMs, idleTimeoutMs }`: 주기·유휴 컷오프를 지정해 활성
     *   (예: `{ heartbeatIntervalMs: 30000, idleTimeoutMs: 300000 }` = 30초 하트비트, 5분 유휴).
     *   idleTimeoutMs 동안 사용자 상호작용이 없으면 하트비트를 멈춰(GA4 정합) 방치 탭의
     *   무한 체류 누적을 막는다(최소: 하트비트 1초, 유휴 10초).
     */
    engagement?: boolean | { heartbeatIntervalMs?: number; idleTimeoutMs?: number };
  };
  ecommerce?: {
    enabled?: boolean;
    currency?: string;
  };
  session?: {
    timeout?: number;
  };
  transport?: {
    method?: 'beacon' | 'fetch' | 'xhr';
    batchSize?: number;
    flushInterval?: number;
  };
  /**
   * 초기 동의 상태 (FRD 38 §10.1/§10.2).
   * 미지정 시 marketing 은 default opt-out(false) 이며,
   * 광고 매칭 식별자(클릭ID) 캡처/반환은 marketing 동의 후에만 수행된다.
   */
  consent?: ConsentSettings;
}

/** 추적 이벤트 데이터 구조 (GA4 호환) */
export interface TrackingEvent {
  event_name: string;
  event_id: string;
  timestamp: number;

  site_id: string;
  visitor_id: string;
  session_id: string;
  user_id?: string;

  page_location: string;
  page_title: string;
  page_referrer: string;

  screen_resolution: string;
  viewport_size: string;
  user_agent: string;
  language: string;

  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;

  event_params: Record<string, unknown>;
  user_properties?: Record<string, unknown>;
}

/** 동의 설정 */
export interface ConsentSettings {
  analytics?: boolean;
  marketing?: boolean;
}

/**
 * 이벤트 훅 컨텍스트 (FRD 38 §2.3).
 *
 * 단일 계측 팬아웃에서 listener(광고 브릿지)가 자체적으로 marketing 동의를
 * 체크할 수 있도록 발사 시점의 consent 스냅샷을 함께 전달한다.
 */
export interface EventHookContext {
  /** 발사 시점의 동의 스냅샷(2축: analytics/marketing). listener 가 marketing 게이팅에 사용. */
  consent: ConsentSettings;
}

/**
 * 이벤트 훅 listener (FRD 38 §2.3 단일 계측 → 팬아웃).
 *
 * track() 이 큐에 이벤트를 적재한 직후 단일 지점에서 호출된다.
 * 광고 브릿지(gtag/fbq/dataLayer)가 이 훅을 구독해 정규화 페이로드로 팬아웃한다.
 * best-effort — listener 예외는 격리되어 /collect 본류·다른 listener 에 전파되지 않는다.
 */
export type EventHook = (event: TrackingEvent, ctx: EventHookContext) => void;

/** 등록된 훅을 해제하는 함수. registerHook() 가 반환한다. */
export type UnregisterHook = () => void;

/**
 * last-touch 어트리뷰션 스냅샷 (U6).
 *
 * lamb-site 의 getOrderTrackingData() 가 그대로 주문/로그인/가입 요청에 실어 보내는
 * camelCase 부분 객체. 모든 필드 optional — 미수집 시 생략된다.
 * (site 의 OrderTrackingData 계약과 키 네이밍 일치 필수.)
 */
export interface TrackingData {
  visitorId?: string;
  sessionId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPage?: string;
  deviceType?: string;
  /**
   * 광고 매칭용 클릭 식별자 (FRD 38 §8.8).
   * fbclid/gclid/wbraid/gbraid 와 _fbc/_fbp 쿠키, 합성 fbc 를 포함하는 부분 객체.
   * 수집값이 하나도 없으면 생략된다.
   */
  clickIds?: ClickIds;
}

export type LaCommand = 'init' | 'track' | 'set' | 'consent' | 'optIn' | 'optOut' | 'ecommerce';

type DataLayerEntry =
  | [command: 'init', siteId: string, options?: LambAnalyticsOptions]
  | [command: 'track', eventName: string, params?: Record<string, unknown>]
  | [command: 'set', key: string, value: unknown]
  | [command: 'consent', settings: ConsentSettings]
  | [command: 'optIn']
  | [command: 'optOut']
  | [command: 'ecommerce', action: string, params: Record<string, unknown>]
  | Record<string, unknown>;

export interface EcommerceApi {
  viewItemList(params: ViewItemListParams): void;
  selectItem(params: SelectItemParams): void;
  viewItem(params: ViewItemParams): void;
  addToWishlist(params: AddToWishlistParams): void;
  addToCart(params: CartEventParams): void;
  removeFromCart(params: CartEventParams): void;
  viewCart(params: CartEventParams): void;
  beginCheckout(params: CheckoutParams): void;
  addShippingInfo(params: ShippingInfoParams): void;
  addPaymentInfo(params: PaymentInfoParams): void;
  purchase(params: PurchaseParams): void;
  refund(params: RefundParams): void;
}

/**
 * 훅 등록 전에 발사된 이벤트를 담아두는 재생 버퍼의 상한(FRD 38 §2.3 보강).
 *
 * 광고 브릿지(lamb-site AdAnalyticsProvider)는 광고설정 fetch 가 끝난 뒤에야 구독하므로
 * 랜딩 첫 page_view / 상품상세 view_item 이 구독 이전에 발사된다. 그 사이 이벤트를 버퍼에
 * 담아 최초 구독자에게 1회 재생한다.
 *
 * 상한을 넘으면 **가장 오래된 것을 버린다** — 광고 팬아웃에는 최근 행동이 더 쓸모 있고,
 * 구독이 이만큼 늦었다면 앞쪽 이벤트는 이미 광고 관점에서 무의미하다.
 * 50 은 "구독 지연 수 초" 동안 한 페이지가 만들 수 있는 이벤트 수(page_view/scroll/click/
 * view_item …)를 넉넉히 덮으면서, 구독자가 영영 안 오는 페이지에서도 메모리가 상수로
 * 묶이는 값이다(무제한 버퍼는 SPA 장시간 세션에서 누수가 된다).
 */
const PENDING_EVENT_BUFFER_LIMIT = 50;

/** 재생 버퍼 항목 — 이벤트와 **발사 시점** consent 스냅샷을 한 쌍으로 굳혀 보관한다. */
interface PendingHookEvent {
  event: TrackingEvent;
  ctx: EventHookContext;
}

export class LambAnalytics {
  private config: SdkConfig | null = null;
  private identity: IdentityManager | null = null;
  private queue: EventQueue | null = null;
  private sender: EventSender | null = null;
  private batch: BatchProcessor | null = null;
  private autoTracker: AutoTrackManager | null = null;
  private productTracker: ProductTracker | null = null;
  private cartTracker: CartTracker | null = null;
  private purchaseTracker: PurchaseTracker | null = null;
  private initialized = false;
  // 동의 기본값은 단일 플래그(CONSENT_DEFAULT_GRANTED, config.ts)가 제어한다.
  //   ⚠️ 임시: 동의 배너 구현 전까지 marketing/analytics 모두 granted(true) 기본 → 배너 없이
  //      광고 매칭 식별자(클릭ID) 캡처/반환·자사 통계가 동작. 프로덕션 전 플래그를 false 로
  //      되돌리면 marketing 은 default opt-out(false)로 복귀하고, 동의 부여 뒤에만 캡처/반환된다
  //      (개인정보보호법 동의분리). host 가 init 에서 consent 객체를 명시하면 이 값을 덮어쓴다.
  private consent: ConsentSettings = {
    analytics: true,
    marketing: CONSENT_DEFAULT_GRANTED,
  };
  private userProperties: Record<string, unknown> = {};
  private debug = false;
  // FRD 38 §2.3 — 단일 계측 팬아웃용 이벤트 훅 listener 배열.
  // track() 의 enqueue 직후 단일 지점에서 호출되며, 광고 브릿지가 구독한다.
  private hooks: EventHook[] = [];
  // 아무도 구독하지 않은 동안 발사된 이벤트 + 그 시점 consent 스냅샷.
  //   배열 = 아직 재생 전(계속 담는다) / null = 재생을 소진했다(다시는 담지도 흘리지도 않는다).
  //   ★ 상태를 **하나로** 둔 이유: 'replayed 플래그 + 버퍼' 두 겹으로 두면 두 가드가 서로를
  //     가려, 한쪽만 지운 변이가 조용히 살아남는다(테스트가 통과하는데 방어는 반쪽).
  private pendingEvents: PendingHookEvent[] | null = [];

  init(siteId: string, options?: LambAnalyticsOptions): void {
    if (this.initialized) {
      this.log('SDK already initialized');
      return;
    }

    this.config = new SdkConfig(siteId, options);
    this.debug = this.config.debug;

    // 초기 동의 상태 반영(FRD 38 §10.1). 미지정 시 default(marketing opt-out) 유지.
    //   consent 는 ConsentSettings 객체여야 한다. boolean 을 받으면 silent drop 하지 않고
    //   normalize(true→두 축 granted / false→두 축 denied)하여 의도-동작 불일치를 방지한다.
    //   그 외 잘못된 타입은 경고만 남기고 default 를 유지한다.
    const rawConsent: unknown = options?.consent;
    if (typeof rawConsent === 'boolean') {
      this.log(`init: boolean consent(${String(rawConsent)}) received — normalizing to both axes`);
      this.consent = { analytics: rawConsent, marketing: rawConsent };
    } else if (rawConsent && typeof rawConsent === 'object') {
      this.consent = { ...this.consent, ...(rawConsent as ConsentSettings) };
    } else if (rawConsent != null) {
      this.log(`init: invalid consent type(${typeof rawConsent}) ignored — keeping default consent`);
    }
    this.identity = new IdentityManager(this.config.session.timeout);
    this.queue = new EventQueue();

    const collectEndpoint = this.resolveEndpoint();
    this.sender = new EventSender(collectEndpoint, this.config.transport.method, this.debug);
    this.batch = new BatchProcessor(
      this.queue,
      this.sender,
      this.config.transport.batchSize,
      this.config.transport.flushInterval,
      this.debug,
    );

    this.batch.start();
    this.initialized = true;

    // U6 last-touch 어트리뷰션 보강 — 진입 1회 캡처.
    //   landing_page: 세션 첫 진입 페이지 고정(sessionStorage).
    //   utm: utm_* 가 있으면 last-touch 로 영속(LS+쿠키, 30일).
    this.identity.captureLandingPage();
    this.identity.captureUtm();
    // P0 광고추적 — 클릭ID last-touch 캡처(fbclid/gclid/wbraid/gbraid + _fbc/_fbp).
    // FRD 38 §10.2 — marketing 동의가 있을 때만 캡처. 미동의 시 setConsent 로
    // marketing 이 부여되는 시점에 캡처한다.
    this.captureClickIdsIfConsented();

    this.autoTracker = new AutoTrackManager();
    this.autoTracker.init(this, this.config);

    this.productTracker = new ProductTracker(this);
    this.cartTracker = new CartTracker(this);
    this.purchaseTracker = new PurchaseTracker(this);

    this.log(`Initialized for site: ${siteId}`);
  }

  track(eventName: string, params: Record<string, unknown> = {}): void {
    if (!this.initialized || !this.config || !this.identity || !this.queue || !this.batch) {
      this.log('SDK not initialized, call la("init", siteId) first');
      return;
    }

    if (!this.consent.analytics) {
      this.log('Analytics consent not granted, skipping event');
      return;
    }

    // 세션 시작 이벤트가 필요한지 확인 — 새 세션 또는 미전송 상태
    if (this.identity.isSessionStartNeeded()) {
      this.enqueueSessionStart();
      this.identity.markSessionStartSent();
    }

    const device = getDeviceInfo();
    const utmParams = getUtmParams();

    const event: TrackingEvent = {
      event_name: eventName,
      event_id: generateEventId(),
      timestamp: Date.now(),

      site_id: this.config.siteId,
      visitor_id: this.identity.getVisitorId(),
      session_id: this.identity.getSessionId(),
      user_id: this.identity.getUserId(),

      page_location: getPageLocation(),
      page_title: getPageTitle(),
      page_referrer: getPageReferrer(),

      screen_resolution: device.screenResolution,
      viewport_size: device.viewportSize,
      user_agent: device.userAgent,
      language: device.language,

      ...utmParams,

      event_params: params,
      user_properties: Object.keys(this.userProperties).length > 0
        ? { ...this.userProperties }
        : undefined,
    };

    this.queue.enqueue(event);
    this.log(`Tracked: ${eventName}`);

    // FRD 38 §2.3 — enqueue 직후 단일 지점에서 이벤트 훅 팬아웃.
    // ecommerce 12메서드가 모두 track() 으로 수렴하므로 여기 한 곳만 후킹한다(메서드별 후킹 금지).
    this.fireHooks(event);

    void this.batch.checkAndFlush();
  }

  set(key: string, value: unknown): void {
    if (key === 'user_id' && typeof value === 'string') {
      if (this.identity) {
        this.identity.setUserId(value);
      }
      this.userProperties[key] = value;
    } else {
      this.userProperties[key] = value;
    }

    this.log(`Set: ${key} = ${String(value)}`);
  }

  /**
   * 이벤트 훅 listener 를 등록한다(FRD 38 §2.3 단일 계측 → 팬아웃).
   *
   * track() 이 큐 적재 직후 단일 지점에서 등록 순서대로 listener 를 호출한다.
   * 광고 브릿지가 이 훅을 구독해 정규화 페이로드로 gtag/fbq/dataLayer 팬아웃을 수행한다.
   *
   * ★ **최초** 구독자는 등록 즉시, 구독 이전에 발사된 이벤트를 발사 순서대로 재생받는다
   *   (replayPendingTo). 두 번째 이후 구독자는 재생받지 않는다 — 중복 집계 방지.
   *   재생된 이벤트의 ctx.consent 는 **발사 시점** 스냅샷이다(재생 시점 값이 아니다).
   *
   * @returns 등록 해제 함수. 동일 listener 를 중복 등록하지 않는다.
   */
  registerHook(hook: EventHook): UnregisterHook {
    if (typeof hook !== 'function') {
      return () => undefined;
    }
    if (!this.hooks.includes(hook)) {
      this.hooks.push(hook);
    }
    this.replayPendingTo(hook);
    return () => {
      const idx = this.hooks.indexOf(hook);
      if (idx !== -1) {
        this.hooks.splice(idx, 1);
      }
    };
  }

  /**
   * 등록된 이벤트 훅을 발사한다(FRD 38 §2.3).
   *
   * best-effort: listener 별로 try/catch 격리하여 한 listener 의 예외가
   * /collect 본류 흐름이나 다른 listener 에 전파되지 않게 한다.
   * 발사 시점의 consent 스냅샷을 ctx 로 함께 전달해 listener 가 marketing 게이팅에 쓸 수 있게 한다.
   */
  private fireHooks(event: TrackingEvent): void {
    // consent 스냅샷은 **여기서** 굳힌다. 버퍼에 넣을 때도 이 값을 그대로 들고 간다 —
    // 재생 시점의 consent 로 갈아끼우면 미동의 상태에서 일어난 행동이 나중 동의로
    // 소급 전송된다(§10.2 위반).
    const ctx: EventHookContext = { consent: { ...this.consent } };

    if (this.hooks.length === 0) {
      // 아직 아무도 구독하지 않았다 — 최초 구독자에게 재생할 수 있게 버퍼에 담는다.
      // 재생을 이미 소진했다면(null) 담지 않는다: 구독자가 있었다가 떠난 상태의
      // 이벤트를 나중 재구독자에게 흘리면 같은 이벤트가 두 번 집계된다.
      const pending = this.pendingEvents;
      if (pending) {
        pending.push({ event, ctx });
        if (pending.length > PENDING_EVENT_BUFFER_LIMIT) {
          // 상한 초과 — 가장 오래된 것부터 버린다(최근 N개를 남긴다).
          pending.shift();
        }
      }
      return;
    }

    // 발사 중 register/unregister 로 인한 배열 변형에 안전하도록 스냅샷을 순회한다.
    for (const hook of this.hooks.slice()) {
      try {
        hook(event, ctx);
      } catch (err) {
        // listener 실패는 격리 — 본류·다른 listener 에 전파 금지.
        this.log(`Event hook listener error: ${String(err)}`);
      }
    }
  }

  /**
   * 버퍼에 쌓인 "구독 이전" 이벤트를 **최초로 등록된 훅 하나에게만, 단 한 번** 재생한다.
   *
   * ★ 일회성이 핵심이다. lamb-site 의 AdAnalyticsProvider effect 는 deps 변화·StrictMode 로
   *   unregister → register 를 다시 탄다. 매번 재생하면 같은 PageView/ViewContent 가 광고
   *   플랫폼에 중복 집계된다. 숫자가 커지는 방향의 고장은 신고가 들어오지 않아 영영 안 보인다.
   *
   * ★ 이 재생 버퍼는 lamb-site `ad-bridge.ts` 의 registerHook 대기 재시도와 한 쌍이다.
   *   브릿지가 SDK 를 기다렸다가 늦게 구독하고(그쪽), 그 사이 이벤트를 여기서 재생한다(이쪽).
   *   한쪽만 있으면 반쪽이다 — 기다리기만 하면 그 사이 이벤트는 여전히 유실되고,
   *   버퍼만 있으면 브릿지가 SDK 부재로 영구 포기해 아무도 재생을 받지 않는다.
   *
   * 재생도 fireHooks 와 동일하게 listener 별 try/catch 로 격리한다(best-effort).
   */
  private replayPendingTo(hook: EventHook): void {
    const pending = this.pendingEvents;
    if (pending === null) {
      return;
    }
    // 재생 중 listener 가 다시 registerHook 을 부르는 재진입에도 두 번 재생되지 않도록
    // 버퍼를 흘리기 **전에** 소진 표시(null)를 못박는다. 빈 배열로 두면 재생이 다시
    // 열려 같은 이벤트가 두 번 집계된다.
    this.pendingEvents = null;

    for (const pendingEvent of pending) {
      try {
        // 발사 시점에 굳혀 둔 ctx 를 그대로 넘긴다(현재 consent 로 갈아끼우지 않는다).
        hook(pendingEvent.event, pendingEvent.ctx);
      } catch (err) {
        this.log(`Event hook replay listener error: ${String(err)}`);
      }
    }
  }

  setConsent(settings: ConsentSettings): void {
    const wasMarketing = this.consent.marketing === true;
    this.consent = { ...this.consent, ...settings };
    this.log(`Consent updated: analytics=${String(this.consent.analytics)}, marketing=${String(this.consent.marketing)}`);

    // marketing 이 새로 부여되면 그 시점에 클릭ID 를 캡처한다(FRD 38 §10.2).
    if (!wasMarketing && this.consent.marketing === true) {
      this.captureClickIdsIfConsented();
    }
  }

  /** marketing 동의 부여 — 광고 매칭 식별자 캡처/반환을 허용한다(FRD 38 §10.2). */
  optIn(): void {
    this.setConsent({ marketing: true });
  }

  /** marketing 동의 철회 — 이후 클릭ID 캡처/반환을 중단한다(FRD 38 §10.2). */
  optOut(): void {
    this.setConsent({ marketing: false });
  }

  /**
   * marketing 동의가 있을 때만 클릭ID last-touch 캡처를 수행한다.
   * best-effort: 실패해도 본류 흐름을 깨지 않게 격리.
   */
  private captureClickIdsIfConsented(): void {
    if (this.consent.marketing !== true || !this.identity) {
      return;
    }
    try {
      this.identity.captureClickIds();
    } catch {
      // 클릭ID 캡처 실패는 조용히 무시.
    }
  }

  get ecommerce(): EcommerceApi {
    const product = this.productTracker;
    const cart = this.cartTracker;
    const purchase = this.purchaseTracker;

    return {
      viewItemList: (p: ViewItemListParams) => product?.viewItemList(p),
      selectItem: (p: SelectItemParams) => product?.selectItem(p),
      viewItem: (p: ViewItemParams) => product?.viewItem(p),
      addToWishlist: (p: AddToWishlistParams) => product?.addToWishlist(p),
      addToCart: (p: CartEventParams) => cart?.addToCart(p),
      removeFromCart: (p: CartEventParams) => cart?.removeFromCart(p),
      viewCart: (p: CartEventParams) => cart?.viewCart(p),
      beginCheckout: (p: CheckoutParams) => purchase?.beginCheckout(p),
      addShippingInfo: (p: ShippingInfoParams) => purchase?.addShippingInfo(p),
      addPaymentInfo: (p: PaymentInfoParams) => purchase?.addPaymentInfo(p),
      purchase: (p: PurchaseParams) => purchase?.purchase(p),
      refund: (p: RefundParams) => purchase?.refund(p),
    };
  }

  /**
   * 큐에 쌓인 이벤트를 즉시 동기 전송한다(sendBeacon).
   *
   * 이탈 추적기가 page_exit 이벤트를 큐에 넣은 직후 호출해, 페이지가 언로드되기 전에
   * 확실히 전송되도록 한다. 큐가 비어있으면 no-op.
   */
  flush(): void {
    if (this.batch) {
      this.batch.flushSync();
    }
  }

  getConfig(): SdkConfig | null {
    return this.config;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * last-touch 어트리뷰션 스냅샷을 동기 조회한다 (U6).
   *
   * lamb-site 가 주문 생성/로그인/가입 직전 호출해 trackingData/visitorId 로 실어 보낸다.
   * - 미초기화 상태면 null.
   * - 값이 없는 필드는 생략(undefined)되며, 부분 객체로 반환된다.
   * - 키는 camelCase(site OrderTrackingData 계약과 일치).
   */
  getTrackingData(): TrackingData | null {
    if (!this.initialized || !this.identity) {
      return null;
    }

    const utm = this.identity.getStoredUtm();

    // P0 광고추적 — 클릭ID 조회(합성 fbc 포함). 비어있으면 생략.
    // FRD 38 §10.2 — marketing 동의가 있을 때만 반환한다(미동의 시 송신 차단).
    let clickIds: ClickIds | undefined;
    if (this.consent.marketing === true) {
      try {
        const stored = this.identity.getStoredClickIds();
        clickIds = Object.keys(stored).length > 0 ? stored : undefined;
      } catch {
        clickIds = undefined;
      }
    }

    const data: TrackingData = {
      visitorId: this.identity.getVisitorId(),
      sessionId: this.identity.getSessionId(),
      utmSource: utm.utm_source,
      utmMedium: utm.utm_medium,
      utmCampaign: utm.utm_campaign,
      utmContent: utm.utm_content,
      utmTerm: utm.utm_term,
      landingPage: this.identity.getLandingPage(),
      deviceType: detectDeviceType(),
      clickIds,
    };

    // undefined 필드 제거 — site 는 부분 객체를 기대한다.
    (Object.keys(data) as (keyof TrackingData)[]).forEach((key) => {
      if (data[key] === undefined) {
        delete data[key];
      }
    });

    return data;
  }

  destroy(): void {
    if (this.autoTracker) {
      this.autoTracker.destroy();
      this.autoTracker = null;
    }
    if (this.batch) {
      this.batch.flushSync();
      this.batch.stop();
    }
    this.initialized = false;
    this.config = null;
    this.identity = null;
    this.queue = null;
    this.sender = null;
    this.batch = null;
    this.productTracker = null;
    this.cartTracker = null;
    this.purchaseTracker = null;
    this.userProperties = {};

    // FRD 38 §2.3 — 훅 구독과 재생 버퍼도 함께 정리한다.
    //   hooks: 남겨두면 destroy → 재init 후 **파괴 전 구독자**가 새 세션의 이벤트를 계속
    //     받는다(유령 구독). track() 이 미초기화 상태에서 조기 반환하는 것에 기대면 안 된다 —
    //     재init 하는 순간 되살아난다.
    //   pendingEvents: **null 이 아니라 빈 배열**로 되돌린다. destroy 는 "초기 상태로
    //     되돌린다"는 뜻이고, 초기 상태는 필드 초기값과 같은 "아직 재생 전"이다.
    //     null(=재생 소진)로 두면 재init 후 랜딩 이벤트가 다시 유실된다 — 이 클래스가
    //     애초에 고치려던 그 버그가 재init 경로에서만 되살아나는 꼴이다.
    this.hooks = [];
    this.pendingEvents = [];
  }

  private resolveEndpoint(): string {
    if (typeof window !== 'undefined' && window.location) {
      return `${window.location.origin}/collect`;
    }
    return '/collect';
  }

  private log(message: string): void {
    if (this.debug && typeof console !== 'undefined') {
      console.debug(`[LambAnalytics] ${message}`);
    }
  }

  /** session_start 이벤트를 큐에 추가 (새 세션 또는 페이지 로드 시 자동 발송) */
  private enqueueSessionStart(): void {
    if (!this.config || !this.identity || !this.queue) return;

    const device = getDeviceInfo();
    const utmParams = getUtmParams();

    const event: TrackingEvent = {
      event_name: 'session_start',
      event_id: generateEventId(),
      timestamp: Date.now(),

      site_id: this.config.siteId,
      visitor_id: this.identity.getVisitorId(),
      session_id: this.identity.getSessionId(),
      user_id: this.identity.getUserId(),

      page_location: getPageLocation(),
      page_title: getPageTitle(),
      page_referrer: getPageReferrer(),

      screen_resolution: device.screenResolution,
      viewport_size: device.viewportSize,
      user_agent: device.userAgent,
      language: device.language,

      ...utmParams,

      event_params: {
        device_type: detectDeviceType(),
        browser: detectBrowser(),
        os: detectOS(),
        is_new_visitor: this.identity.isNewVisitor(),
      },
    };

    this.queue.enqueue(event);
    this.log('Tracked: session_start (auto)');
  }
}

// --- Global la() function registration ---

/** 전역 `la` 디스패처 + U6 동기 조회 표면 + §2.3 훅 구독 표면 */
type LaDispatcher = ((...args: unknown[]) => void) & {
  getTrackingData?: () => TrackingData | null;
  registerHook?: (hook: EventHook) => UnregisterHook;
};

declare global {
  interface Window {
    la: LaDispatcher;
    laDataLayer: DataLayerEntry[];
    LambAnalytics: typeof LambAnalytics;
  }
}

const sdk = new LambAnalytics();

function processCommand(args: unknown[]): void {
  const command = args[0] as string;

  switch (command) {
    case 'init':
      sdk.init(args[1] as string, args[2] as LambAnalyticsOptions | undefined);
      break;
    case 'track':
      sdk.track(args[1] as string, (args[2] as Record<string, unknown>) ?? {});
      break;
    case 'set':
      sdk.set(args[1] as string, args[2]);
      break;
    case 'consent':
      sdk.setConsent(args[1] as ConsentSettings);
      break;
    case 'optIn':
      sdk.optIn();
      break;
    case 'optOut':
      sdk.optOut();
      break;
    case 'ecommerce': {
      const action = args[1] as string;
      const params = (args[2] as Record<string, unknown>) ?? {};
      const api = sdk.ecommerce;
      if (action in api) {
        (api as unknown as Record<string, (p: Record<string, unknown>) => void>)[action](params);
      }
      break;
    }
    default:
      break;
  }
}

function la(...args: unknown[]): void {
  processCommand(args);
}

// U6 — 동기 조회 표면을 전역 디스패처에 부착(site getOrderTrackingData 가 사용).
// §2.3 — 훅 구독 표면을 전역 디스패처에 부착(광고 브릿지가 listener 등록에 사용).
Object.assign(la, {
  getTrackingData: (): TrackingData | null => sdk.getTrackingData(),
  registerHook: (hook: EventHook): UnregisterHook => sdk.registerHook(hook),
});

// Process queued commands from laDataLayer (pre-init snippet)
if (typeof window !== 'undefined') {
  const dataLayer = window.laDataLayer;
  if (Array.isArray(dataLayer)) {
    for (const entry of dataLayer) {
      // 부트스트랩 스텁이 `arguments`(array-like, Array.isArray=false)를 push 할 수 있으므로
      // 실배열뿐 아니라 유사배열도 수용한다(GA dataLayer 관례와 동일). Array.isArray 만
      // 검사하면 스텁이 큐잉한 init 커맨드가 유실되어 SDK 가 초기화되지 않는다.
      if (Array.isArray(entry)) {
        processCommand(entry as unknown[]);
      } else if (
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { length?: unknown }).length === 'number'
      ) {
        processCommand(Array.prototype.slice.call(entry as unknown as ArrayLike<unknown>));
      }
    }
  }

  window.la = la;
  window.LambAnalytics = LambAnalytics;
}

export { la, sdk };
